/**
 * @file Unit entity: wraps a unit payload (`data`) and exposes its fields
 * (attachments included), its store relations (property, contracts,
 * subscription, default and non-default charge templates, agency fees), its
 * derived status flags (rented / expiring / ending / empty / inactive / paid
 * states) and display names — including `ownerName`, the unit's property-owner
 * company name.
 *
 * Flow: `UnitFactory` builds it from the backend unit payload → getters resolve
 * relations live from the store (`properties.byId`, `app.contracts`,
 * `app.chargeTemplates`, …) → pages and partials read the getters.
 * Exceptions: none — relation getters return undefined/empty for a missing
 * relation; `ownerName` falls back to an empty string.
 */
import { Entity } from "../../Entity";
import UnitFactory from "../../../Entity/Factories/UnitFactory";
import { FactoryInterface } from "../../../Entity/FactoryInterface";
import Image from "../../../Image/Image";
import { UnitInterface } from "./UnitInterface";
import { PropertyInterface } from "../Property/PropertyInterface";
import { AttachmentInterface } from "../Attachment/AttachmentInterface";
import { ContractInterface } from "../Contract/ContractInterface";
import { SubscriptionInterface } from "../Subscription/SubscriptionInterface";
import { ChargeTemplateInterface } from "../Charge/ChargeTemplateInterface";
import { AgencyFeeInterface } from "../AgencyFee/AgencyFeeInterface";
import AgencyFeeContainer from "../../../Entity/Entities/AgencyFee/AgencyFeeContainer";
import SubscriptionTypeContainer from "../../../Entity/Entities/Subscription/SubscriptionType/SubscriptionTypeContainer";
import Translator from "../../../Translation/Translator";
import UnitStatusTypeContainer from "./Status/UnitStatusTypeContainer";
import UnitPaidStatusTypeContainer from "./PaidStatus/UnitPaidStatusTypeContainer";

class Unit extends Entity implements UnitInterface {

  constructor(public data) {
    super();
  }

  get nameOfEntity() {
    return Translator.trans("unit");
  }

  get nameForUrl() {
    return "unit";
  }

  get labelForEntity() {
    return "label-unit"
  }

  public getFactory(): FactoryInterface {
    return UnitFactory;
  }

  get attachments(): AttachmentInterface[] {
    return this.data.attachments;
  }

  get id(): number {
    return this.data.id;
  }

  get avatar(): string {
    return this.data.avatar;
  }

  get createdBy(): number {
    return this.data.createdBy;
  }

  get updatedBy(): number {
    return this.data.updatedBy;
  }

  get name(): string {
    return this.data.name;
  }

  get contractIds(): number[] {
    return this.data.contractIds;
  }

  get contracts(): ContractInterface[] {
    return this.relations("app.contracts", "app.contractsInUnits", this.data.id);
  }

  get subscription(): SubscriptionInterface {
    return this.subscriptionRelation(SubscriptionTypeContainer.pickIdFromLabel("Units"), this.data.id);
  }

  get updatedAt(): Date {
    return this.data.updatedAt;
  }

  get isSubscribed(): boolean {
    return this.subscription !== null;
  }

  get unitNumber(): string {
    return this.data.unitNumber;
  }

  get squareMeters(): number {
    return this.data.squareMeters;
  }

  get roomNumber(): number {
    return this.data.roomNumber;
  }

  get bedroomNumber(): number {
    return this.data.bedroomNumber;
  }

  get bathroomNumber(): number {
    return this.data.bathroomNumber;
  }

  get description(): string {
    return this.data.description;
  }

  get publicationTitle(): string {
    return this.data.publicationTitle;
  }

  get publicationDescription(): string {
    return this.data.publicationDescription;
  }

  get displayName(): string {
    const propertyAddress = this.property && this.property.displayName ?
      this.property.displayName : "";
    const displayName: string = this.data.unitNumber ?
      `${this.data.unitNumber}, ${propertyAddress}` : `${propertyAddress}`;
      // this.property?.address?.formattedAddress returns address with house number and letter extention(Snellestraat 35a)
    // return this.property?.address?.formattedAddress || displayName;
    return this.data.displayName || displayName;
  }

  get listDisplayName(): string {
    return this.nameOfEntity + ", " + this.displayName;
  }

  get propertyId(): number {
    return this.data.propertyId;
  }

  get property(): PropertyInterface {
    return this.newRelation("properties.byId", this.data.propertyId)[0];
  }

  get ownerName(): string {
    return this.property?.company?.name || "";
  }

  get defaultChargeTemplates(): ChargeTemplateInterface[] {

    return this.all("app.chargeTemplates")
      .filter((chargeTemplate: ChargeTemplateInterface) => !!chargeTemplate &&
                                                           !chargeTemplate.removed &&
                                                           chargeTemplate.isDefault &&
                                                           chargeTemplate.unitId === this.data.id)
      .sort((template1: ChargeTemplateInterface, template2: ChargeTemplateInterface) => {
        if (template1.creationDate > template2.creationDate) {
          return 1;
        }

        if (template1.creationDate < template2.creationDate) {
          return -1;
        }

        return  0;
      });
  }

  get chargeTemplates(): ChargeTemplateInterface[] {
    return this.all("app.chargeTemplates")
      .filter((chargeTemplate: ChargeTemplateInterface) => !!chargeTemplate &&
                                                           !chargeTemplate.removed &&
                                                           !chargeTemplate.isDefault &&
                                                           chargeTemplate.unitId === this.data.id)
      .sort((template1: ChargeTemplateInterface, template2: ChargeTemplateInterface) => {
        if (template1.creationDate > template2.creationDate) {
          return 1;
        }

        if (template1.creationDate < template2.creationDate) {
          return -1;
        }

        return  0;
      });
  }

  get defaultAgencyFees(): AgencyFeeInterface[] {
    return this.all("app.agencyFees")
    .filter((agencyFee: AgencyFeeInterface) =>
                                        agencyFee.removed === false &&
                                        agencyFee.objectType === AgencyFeeContainer.pickIdFromLabel("Unit") &&
                                        agencyFee.objectId === this.data.id);
  }

  get inheritedAgencyFee(): AgencyFeeInterface | null {
    if (this.property && this.property.defaultAgencyFees.length > 0) {
      return this.property.defaultAgencyFees[0];
    }

    if (this.property && this.property.company && this.property.company.defaultAgencyFees.length > 0) {
      return this.property.company.defaultAgencyFees[0];
    }

    if (this.property && this.property.company && this.property.company.agency && this.property.company.agency.defaultAgencyFees.length > 0) {
      return this.property.company.agency.defaultAgencyFees[0];
    }

    return null;
  }

  get type(){
    return this.data.type;
  }

  get removed(): boolean {
    return this.data.removed;
  }

  public getActiveContract(): ContractInterface {
    const activeContracts: ContractInterface[] = this.contracts
      .filter((contract: ContractInterface) => !contract.removed && contract.isActive)
      .sort((contract1: ContractInterface, contract2: ContractInterface) => {
        if (contract1.firstDate < contract2.firstDate) {
          return 1;
        }

        if (contract1.firstDate > contract2.firstDate) {
          return -1;
        }

        return  0;
      });

    return activeContracts[0];
  }

  public getMostRecentNonClosedContract(): ContractInterface {
    const activeContracts: ContractInterface[] = this.contracts
      .filter((contract: ContractInterface) => contract.removed === false)
      .sort((contract1: ContractInterface, contract2: ContractInterface) => {
        if (contract1.firstDate < contract2.firstDate) {
          return 1;
        }

        if (contract1.firstDate > contract2.firstDate) {
          return -1;
        }

        return  0;
      })
      .filter((contract: ContractInterface) => {
        if ((contract.isActive || contract.isConcept)) {
          return true;
        }
      });

    return activeContracts[0];
  }
  // public getContractsAdjustRent(): ContractInterface {
  //   const activeContracts: ContractInterface[] = this.contracts
  //     .filter((contract: ContractInterface) => contract.removed === false)
  //     .sort((contract1: ContractInterface, contract2: ContractInterface) => {
  //       if (contract1.firstDate < contract2.firstDate) {
  //         return 1;
  //       }

  //       if (contract1.firstDate > contract2.firstDate) {
  //         return -1;
  //       }

  //       return  0;
  //     })
  //     .filter((contract: ContractInterface) => {
  //       if ((contract.isActive || contract.isConcept)) {
  //         return true;
  //       }
  //     });

  //   return activeContracts;
  // }

  public getTenantAndOccupantsForUnitList(): any {
    const activeContract: ContractInterface = this.getActiveContract();

    if (!activeContract) {
      return null;
    }

    return activeContract.occupants;
  }

  get note(): string {
    return this.data.note;
  }

  get unitStatusId(): number {
    return this.data.unitStatusId;
  }

  get unitStatus(): string {
    return UnitStatusTypeContainer.pickLabelFromId(this.unitStatusId)?.toLowerCase();
  }

  get paidStatusId(): number {
    return this.data.paidStatusId;
  }

  get unitPaidStatus(): string {
    return UnitPaidStatusTypeContainer.pickLabelFromId(this.paidStatusId)?.toLowerCase();
  }

  get isRented(): boolean {
    return this.unitStatus === "rented";
  }

  get isExpiring(): boolean {
    return this.unitStatus === "expiring";
  }

  get isEnding(): boolean {
    return this.unitStatus === "ending";
  }

  get isEmpty(): boolean {
    return this.unitStatus === "empty";
  }

  get isInactive(): boolean {
    return this.unitStatus === "inactive";
  }

  get isPaid(): boolean {
    return this.unitPaidStatus === "paid";
  }

  get isPartlyUnpaid(): boolean {
    return this.unitPaidStatus === "partly_unpaid";
  }

  get isUnpaid(): boolean {
    return this.unitPaidStatus === "unpaid";
  }

  get planningData(): any[] {
    return this.data.planningData;
  }

  get planningDataNormalized(): any[] {
    const dataArr = [];

    for (let key in this.planningData) {
      let color = "#F5A623"; //yellow

      if (this.planningData[key] === 0) {
        color = "#F44336"; //red
      } else if (this.planningData[key] === 1) {
        color = "#4CAF50"; //green
      }

      dataArr.push(color);
    }

    return dataArr;
  }

  public renderAvatar(params: any = {}): any {
    return Image.getAvatarAbsolutely(this.data.avatar, "unit", params);
  }

  get countOfUnfinishedTasks(): number {
    return this.data.countOfUnfinishedTasks;
  }

  get countOfOverdueTasks(): number {
    return this.data.countOfOverdueTasks;
  }

  get rentUpdateDate(): Date {
    return this.data.rentUpdateDate;
  }

  get unitServiceStatus(): string {
    return this.data.unitServiceStatus;
  }

  get existsActiveAgencyFees(): boolean {
    return this.data.existsActiveAgencyFees;
  }

  get hasMemos(): boolean {
    return this.data.hasMemos;
  }

  get isNeedToAdjustRent(): boolean {
    return this.data.isNeedToAdjustRent;
  }

  get shortDisplayName(): string {
    return this.data.shortDisplayName;
  }
  
  get isReadonly(): boolean {
    return this.removed;
  }

  // get unitShortDisplayName(): string {
  //   const streetName = this.property?.address?.route ? `${this.property?.address?.route} ` : "";
  //   const houseNumber = this.data.unitNumber ? `${this.data.unitNumber} ` : "";
  //   const city = this.property?.address?.locality ? this.property?.address?.locality : "";
  //   return `${streetName}${houseNumber}${(streetName || houseNumber) && ", "}${city}`;
  // }
}

export { Unit };
